import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { getBaseUrl } from './ApiContext';
import { startSession } from './session';

const AuthContext = createContext(null);

const TEAM_DOMAIN_SUFFIX = '@kyberknight.com';

// Google caps an implicit-flow access token at one hour and issues no refresh
// token with it, so there is no expiry setting to turn up: the only way to stay
// signed in past the hour is to quietly ask Google for a new token before the
// old one dies. That is what the renewal below does.
//
// It renews a few minutes early because a token that expires mid-request still
// costs a bounce to /login, and it only ever attempts a SILENT renewal -- see
// the prompt comment on requestRenewal. If the silent path is refused or
// blocked, nothing is broken: the old token stays in place and the 401 handler
// sends the user to /login exactly as it did before any of this existed.
//
// That refusal turned out to be the common case rather than the edge, which is
// why session.js exists. A silent renewal still opens a popup, and browsers
// block popups that no click asked for, so in practice the hour ran out and the
// account picker came back several times a day. What the browser holds now is
// normally the backend's own session token, good for a day, and the timings
// below are unchanged because they read the stored expiry rather than assuming
// an hour: they simply come round once a day instead of once an hour.
const TOKEN_EXPIRY_KEY = 'tokenExpiry';
const RENEW_MARGIN_MS = 5 * 60 * 1000;
const RENEW_CHECK_MS = 30 * 1000;
// After a refusal, wait before trying again. Without this a signed-out Google
// session would retry every RENEW_CHECK_MS for as long as the tab is open.
const RENEW_BACKOFF_MS = 60 * 1000;
// requestAccessToken() is a no-op if the Google script has not loaded yet, and
// in that case NO callback ever fires. Treat an attempt older than this as
// finished so one no-op cannot wedge renewal for the life of the tab.
const RENEW_DEADLINE_MS = 60 * 1000;
// Sessions that predate this change have no stored expiry. They were minted
// under the same one-hour cap, so authTimestamp dates them well enough to
// renew, rather than being dropped at their next 401.
const ASSUMED_TOKEN_LIFETIME_MS = 60 * 60 * 1000;

// When the current token dies, in epoch ms, or null if we cannot tell.
const storedTokenExpiry = () => {
    const explicit = parseInt(localStorage.getItem(TOKEN_EXPIRY_KEY) || '', 10);
    if (!Number.isNaN(explicit)) {
        return explicit;
    }
    const issued = parseInt(localStorage.getItem('authTimestamp') || '', 10);
    if (!Number.isNaN(issued)) {
        return issued + ASSUMED_TOKEN_LIFETIME_MS;
    }
    return null;
};

// The backend now rejects requests with an expired/invalid token (401) --
// previously it accepted anything, so a stale token just silently kept
// working. Clear the stale session and force a fresh login rather than
// leaving the app showing empty data with no explanation. Exported so the
// handful of raw fetch() calls that bypass axios can reuse the same logic.
export const forceReauth = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('authTimestamp');
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    delete axios.defaults.headers.common['Authorization'];
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

// Registered once at module load (not per-render) so every axios call in the
// app -- regardless of which component made it -- gets the same 401 handling.
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            forceReauth();
        }
        return Promise.reject(error);
    }
);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        const storedAuth = localStorage.getItem('isAuthenticated');
        console.log('Initial isAuthenticated:', storedAuth);
        return storedAuth === 'true';
    });

    const [accessToken, setAccessToken] = useState(() => {
        const token = localStorage.getItem('accessToken');
        console.log('Initial accessToken:', token ? 'Present' : 'Not present');
        // Set synchronously during initial render, not in a useEffect below --
        // a child component's own data-fetch effect can otherwise run before
        // this provider's effect does, firing the first request with no
        // Authorization header and tripping the 401 handler right after login.
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        return token || null;
    });

    const [user, setUser] = useState(() => {
        const storedUser = localStorage.getItem('user');
        console.log('Initial stored user:', storedUser);
        try {
            const parsedUser = storedUser ? JSON.parse(storedUser) : null;
            console.log('Parsed user:', parsedUser);
            return parsedUser;
        } catch (error) {
            console.error('Error parsing stored user:', error);
            return null;
        }
    });

    useEffect(() => {
        console.log('isAuthenticated changed:', isAuthenticated);
        localStorage.setItem('isAuthenticated', isAuthenticated.toString());
    }, [isAuthenticated]);

    useEffect(() => {
        console.log('User state updated:', user);
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
        } else {
            localStorage.removeItem('user');
        }
    }, [user]);

    // expiresInSeconds comes from Google with a freshly minted token. It is
    // deliberately optional: AuthenticatedApp re-calls login() on every mount
    // to rehydrate from localStorage, and that call must not overwrite the
    // real expiry of the token it is rehydrating with a made-up one.
    const login = useCallback((token, userData, expiresInSeconds) => {
        console.log('Login called with:', { token, userData });
        if (!token || !userData) {
            console.error('Invalid login data');
            return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setIsAuthenticated(true);
        setAccessToken(token);
        setUser(userData);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('accessToken', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('authTimestamp', Date.now().toString());
        if (typeof expiresInSeconds === 'number' && expiresInSeconds > 0) {
            localStorage.setItem(
                TOKEN_EXPIRY_KEY,
                (Date.now() + expiresInSeconds * 1000).toString()
            );
        }
    }, []);

    const logout = useCallback(() => {
        console.log('Logout called');
        delete axios.defaults.headers.common['Authorization'];
        setIsAuthenticated(false);
        setAccessToken(null);
        setUser(null);
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        localStorage.removeItem('authTimestamp');
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
    }, []);

    // When the current silent renewal started (0 when none is running), and
    // when the next one is allowed to start after a refusal.
    const renewStartedAt = useRef(0);
    const renewBlockedUntil = useRef(0);
    const userRef = useRef(user);
    userRef.current = user;

    const renewalRefused = useCallback((reason, detail) => {
        console.warn('Silent token renewal not available:', reason, detail || '');
        renewStartedAt.current = 0;
        renewBlockedUntil.current = Date.now() + RENEW_BACKOFF_MS;
    }, []);

    const requestRenewal = useGoogleLogin({
        flow: 'implicit',
        onSuccess: async (tokenResponse) => {
            renewStartedAt.current = 0;
            const token = tokenResponse && tokenResponse.access_token;
            if (!token) {
                renewalRefused('no token in response');
                return;
            }
            const previousEmail = ((userRef.current && userRef.current.email) || '').toLowerCase();
            try {
                const resp = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const info = await resp.json();
                if (!resp.ok) {
                    renewalRefused('userinfo rejected the renewed token', info);
                    return;
                }
                const email = (info.email || '').toLowerCase();
                // A renewal must never be able to change WHO you are. The
                // backend reads g.user_email straight off this token and every
                // per-user route trusts it, so a token that came back for a
                // different account would quietly point this session at another
                // teammate's records. hint should prevent it; this is the check
                // that makes it impossible rather than unlikely.
                if (!email.endsWith(TEAM_DOMAIN_SUFFIX) || (previousEmail && email !== previousEmail)) {
                    console.error('Renewed token belongs to a different account, signing out');
                    forceReauth();
                    return;
                }
                console.log('Access token renewed silently');
                // Same trade as the sign-in button makes: this Google token
                // is worth an hour and has just re-proved who this is, so
                // spend it on a session token worth a day rather than
                // putting the app back on the hourly clock.
                const session = await startSession(
                    token,
                    getBaseUrl(),
                    tokenResponse.expires_in
                );
                login(session.token, info, session.expiresIn);
            } catch (error) {
                renewalRefused('could not confirm the renewed token', error);
            }
        },
        // Google refused to issue a token without asking the user something --
        // signed out, consent revoked, a different account. Keep the existing
        // token; the 401 handler takes it from here.
        onError: (error) => renewalRefused('google declined', error),
        // The popup could not open or was closed. Popup blockers land here.
        onNonOAuthError: (error) => renewalRefused('popup unavailable', error),
    });

    const maybeRenew = useCallback(() => {
        if (localStorage.getItem('isAuthenticated') !== 'true') {
            return;
        }
        if (!localStorage.getItem('accessToken')) {
            return;
        }
        // A silent renewal still opens a Google popup, which normally closes
        // itself in well under a second. Browsers block popups opened by a
        // hidden tab, and one appearing over whatever the user is doing in
        // another window would be worse than the wait, so renewal only runs
        // while this tab is on screen. The visibilitychange listener below is
        // what covers the ordinary case of a laptop closed and reopened, where
        // the interval has been asleep and the token expired meanwhile.
        if (document.visibilityState !== 'visible') {
            return;
        }
        const now = Date.now();
        if (now < renewBlockedUntil.current) {
            return;
        }
        if (renewStartedAt.current && now - renewStartedAt.current < RENEW_DEADLINE_MS) {
            return;
        }
        const expiry = storedTokenExpiry();
        if (expiry === null || now < expiry - RENEW_MARGIN_MS) {
            return;
        }
        renewStartedAt.current = now;
        const hint = (userRef.current && userRef.current.email) || '';
        // prompt '' is the whole point: if this browser is still signed in to
        // Google and has already granted this app, hand back a token without
        // asking the user anything. hint names the account, so a browser signed
        // in to several does not stop to ask which one.
        requestRenewal(hint ? { prompt: '', hint } : { prompt: '' });
    }, [requestRenewal]);

    useEffect(() => {
        if (!isAuthenticated) {
            return undefined;
        }
        maybeRenew();
        const interval = setInterval(maybeRenew, RENEW_CHECK_MS);
        document.addEventListener('visibilitychange', maybeRenew);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', maybeRenew);
        };
    }, [isAuthenticated, maybeRenew]);

    // A hard cap on the session regardless of renewal. Note that login() resets
    // authTimestamp, and a successful renewal calls login(), so in practice
    // this only fires for a tab that has been open and failing to renew.
    // It is now the same 24 hours as the session token's own life, which is
    // an agreement rather than a coincidence: if this number is changed,
    // change SESSION_MAX_AGE_SECONDS in the backend's session_token.py with
    // it, or one of the two will cut the day short with no explanation.
    useEffect(() => {
        const checkAuthExpiration = () => {
            const authTimestamp = localStorage.getItem('authTimestamp');
            if (authTimestamp) {
                const currentTime = Date.now();
                const authTime = parseInt(authTimestamp, 10);
                const timeDifference = currentTime - authTime;
                const hoursDifference = timeDifference / (1000 * 60 * 60);

                if (hoursDifference > 24) {
                    console.log('Auth expired, logging out');
                    logout();
                }
            }
        };

        const interval = setInterval(checkAuthExpiration, 60000);
        return () => clearInterval(interval);
    }, [logout]);

    const value = {
        isAuthenticated,
        accessToken,
        user,
        login,
        logout,
    };

    console.log('AuthContext value:', value);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
